import React, { useState, useEffect, useRef } from 'react';
import { MdShoppingCart, MdClose } from 'react-icons/md';
import { useNavigate, Link } from 'react-router-dom';
import ResponsiveButton from '../../components/ResponsiveButton/ResponsiveButton';
import { motion, AnimatePresence } from 'framer-motion';

const cartVariants = {
  hidden: { opacity: 0, x: 100 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 }
};

const MiniCart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    fetch("https://spiderlegsproductions.com/api/get_vuokrattavat.php")
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.items)) {
          setAllProducts(data.items);
        }
      })
      .catch(err => console.error("Virhe tuotteiden haussa:", err));
  }, []);

  useEffect(() => {
    const updateCart = () => {
      const cart = JSON.parse(localStorage.getItem('rentalCart')) || [];
      if (!allProducts.length) {
        setCartItems(cart);
        return;
      }
      const enhanced = cart.map(item => {
        const full = allProducts.find(p => p.id === item.id);
        return full
          ? { ...item, image: full.image || '' } // Älä ylikirjoita name tai price
          : item;
      });
      
      
      setCartItems(enhanced);
    };

    updateCart();
    window.addEventListener('cartUpdated', updateCart);
    return () => window.removeEventListener('cartUpdated', updateCart);
  }, [allProducts]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const removeItemFromCart = (productId) => {
    const updatedCart = cartItems.filter(item => item.id !== productId);
    localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
    setCartItems(updatedCart);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative dark:text-primary text-darkest hover:text-linkcol focus:outline-none"
        title="Näytä vuokrauskori"
      >
        <MdShoppingCart size={32} />
        {cartItems.length > 0 && (
          <span className="absolute top-0 right-0 bg-red-600 text-white rounded-full text-xs px-1">
            {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
          key="cart"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={cartVariants}
          transition={{ duration: 0.3 }}
          layout
          className="fixed right-0 top-20 w-72 sm:w-80 md:w-96 max-h-[80vh] overflow-y-auto bg-white dark:bg-violet shadow-2xl z-50 p-4 rounded-bl-lg"

          >
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-customFont2 text-2xl font-bold text-darkest dark:text-primary dark:text-shadow-xxl">
                Vuokrauskori
              </h4>
              <button onClick={() => setOpen(false)} title="Sulje vuokrauskori">
                <MdClose size={22} className="text-darkest dark:text-primary" />
              </button>
            </div>

            <hr className="my-3 border-t border-gray-800 dark:border-gray-600" />

            {cartItems.length === 0 ? (
              <p className="font-customFont1 text-darkest dark:text-shadow-lg dark:text-primary text-sm font-light">Vuokrauskori on tyhjä.</p>
            ) : (
              <>
                <ul className="divide-y divide-gray-300 dark:divide-gray-600 max-h-64 sm:max-h-80 overflow-y-auto mb-4 pr-1">
                  {cartItems.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 py-2 relative">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-14 h-14 object-cover shadow-sm bg-white rounded-sm"
                      />
                      <div className="flex-1 pr-6">
                        <Link
                          to={`/tuote/${item.slug}`}
                          onClick={() => setOpen(false)}
                          className="block font-customFont1 xxxsm:text-xs text-sm text-darkest dark:text-shadow-lg hover:underline hover:text-gray-600 break-words leading-tight"
                        >
                          {item.name}
                        </Link>
                        <div className="font-customFont1 text-base text-darkest dark:text-primary font-semibold mt-1 dark:text-shadow-lg">
                          {item.quantity > 1
                            ? `${item.quantity} x ${item.price} € = ${item.totalPrice} €`
                            : `${item.price} €`}
                        </div>

                        {/* ⏱ Näytetään vuokra-aikaväli, jos saatavilla */}
                        {item.startDate && item.endDate && (
                          <div className="text-xs text-darkest dark:text-primary mt-1 italic leading-tight">
                            {new Date(item.startDate).toLocaleString("fi-FI", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}<br />
                            – {new Date(item.endDate).toLocaleString("fi-FI", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => removeItemFromCart(item.id)}
                        className="absolute top-1 right-0 text-darkest dark:text-primary hover:text-red-600 dark:hover:text-red-600 transform transition duration-200 hover:scale-110"
                        title="Poista tuote"
                      >
                        <MdClose size={16} />
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="text-right font-customFont1 dark:text-shadow-lg font-bold text-base text-darkest dark:text-primary mb-3">
                    Yhteensä: {
                    cartItems.reduce((sum, item) => {
                        let price = 0;
                    
                        if (item.startDate && item.endDate && item.vaatii_ajan === 1) {
                        const start = new Date(item.startDate);
                        const end = new Date(item.endDate);
                    
                        // Laske erotus päivinä
                        const durationInDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                        const dailyPrice = Number(item.price);
                        price = durationInDays * dailyPrice;
                        } else {
                        price = Number(item.totalPrice) || (Number(item.price) * Number(item.quantity)) || 0;
                        }
                    
                        return sum + price;
                    }, 0)
  
                    } €
                    </div>

                    <ResponsiveButton
                  onClick={() => {
                    setOpen(false);
                    navigate('/tee-varaus');
                  }}
                  variant="proceedtocheckout"
                  className=""
                >
                  Siirry varaamaan
                </ResponsiveButton>

              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MiniCart;
